'use client';

import { useState } from 'react';
import { ArrowsClockwise, CaretDown, Check, Copy, WarningCircle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import type { ChatErrorInfo } from '@/lib/chat-error-format';

function CopyChip({ label, value, title }: { label: string; value: string; title: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={title}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="inline-flex items-center gap-1 rounded-md border border-border/50 bg-muted/30 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      {copied ? <Check weight="bold" className="size-2.5 text-emerald-500" aria-hidden="true" /> : <Copy weight="bold" className="size-2.5" aria-hidden="true" />}
      {label}
    </button>
  );
}

interface ChatErrorPanelProps {
  error: ChatErrorInfo;
  canRetryFromFailure: boolean;
  onRetry: () => void;
  onDismiss: () => void;
}

export function ChatErrorPanel({
  error,
  canRetryFromFailure,
  onRetry,
  onDismiss,
}: ChatErrorPanelProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto flex max-w-2xl items-start gap-3 rounded-xl border border-destructive/20 bg-destructive/[0.04] px-4 py-3"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="shrink-0 flex size-9 items-center justify-center rounded-xl border border-destructive/30 bg-destructive/10">
        <WarningCircle weight="duotone" className="size-5 text-destructive" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium text-foreground/95">{error.message}</p>
        {error.hint && (
          <p className="mt-0.5 text-xs text-muted-foreground/75 leading-relaxed">{error.hint}</p>
        )}
        {(error.requestId || (error.details && error.details !== error.message)) && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {error.requestId && (
              <CopyChip label={`ref ${error.requestId.slice(0, 8)}`} value={error.requestId} title="Copy the full reference ID" />
            )}
            {error.details && error.details !== error.message && (
              <details className="group min-w-0">
                <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-md py-0.5 text-xs font-medium text-muted-foreground/70 hover:text-foreground">
                  Details
                  <CaretDown weight="bold" className="size-3 transition-transform group-open:rotate-180" />
                </summary>
                <div className="mt-1">
                  <pre className="max-h-48 overflow-auto rounded-lg border border-border/40 bg-black/20 p-2 text-[11px] leading-relaxed text-muted-foreground/90">
                    {error.details}
                  </pre>
                  <div className="mt-1">
                    <CopyChip label="copy details" value={error.details} title="Copy the raw error for a bug report" />
                  </div>
                </div>
              </details>
            )}
          </div>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-1">
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] px-3 py-1.5 text-xs font-semibold text-white shadow-[0_4px_16px_rgba(59,91,217,0.3)] transition-all hover:scale-[1.02]"
        >
          <ArrowsClockwise weight="bold" className="size-3" aria-hidden="true" />
          {canRetryFromFailure ? 'Continue' : 'Retry'}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-lg px-2 py-1.5 text-xs text-muted-foreground/70 transition-colors hover:bg-muted/40 hover:text-foreground"
        >
          Dismiss
        </button>
      </div>
    </motion.div>
  );
}
